"use client"

import Image from "next/image"
import { useLocale } from "next-intl"
import { useTranslations } from "next-intl"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry } from "@/lib/country-gates"
import { track } from "@/lib/analytics"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { goKeys as k } from "@/i18n/go-keys"
import {
  bookingUrlForCountry,
  PRODUCT_IDS,
  BOOKING_PACKAGE_TYPE,
} from "@/lib/booking"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { SproutIcon } from "@/components/ui/icons"
import { BookingCta } from "@/components/ui/booking-cta"
import { QuizCtaLink } from "@/components/quiz/quiz-cta-link"
import { BloodTrackingComparison } from "@/components/ui/blood-tracking-comparison"
import { ImmersiveReveal } from "@/components/ui/immersive-reveal"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import { baseBloodTest } from "@/lib/base-blood-test"
import { PHOTOS, STEPS, REVEAL_ROWS } from "./lauraphilipps-data"

/**
 * Soft secondary CTA beside the hero's booking button — the homepage's "How it
 * works" treatment (outline button, white text, translucent white border over
 * the photo), pointed at the `/quiz-focus` funnel instead.
 *
 * RENDERS NOTHING where that funnel does not exist. `/quiz-focus` is gated by
 * `page:quiz` (CH, NL and DE; AT still needs the de-AT quiz namespace), so a
 * market without it would get a link to a 404. Reading the gate rather than
 * testing the country is why the button appeared on the German page by itself
 * when Germany got the funnel.
 */
export function AthleteQuizCta() {
  const t = useTranslations("go")
  const { code: country } = useCountry()
  if (!isAllowedInCountry("page:quiz", country)) return null
  return (
    <QuizCtaLink
      className="block w-full sm:inline-block sm:w-auto"
      onClick={() => track.ctaClick("Find the right check-up", "hero")}
    >
      <Button
        size="lg"
        variant="outline"
        className="w-full border-white/30 text-white hover:border-white/30 hover:bg-white/10 sm:w-auto"
      >
        {t(k.athleteQuizCta)}
      </Button>
    </QuizCtaLink>
  )
}

/** Shared content column — same 1440/40px grid as the sibling concern LPs. */
export const SECTION = "mx-auto max-w-[1440px] px-6 md:px-10"

/**
 * Booking destination for the headline offer: the Advanced package (bloods +
 * full-body MRI) opened on the MRI package tab. Every "full check-up" CTA on
 * this page lands here, so the pitch and the booking flow agree.
 */
export function useFullCheckupHref(): string {
  const locale = useLocale()
  const { code: country } = useCountry()
  return bookingUrlForCountry(
    country,
    locale,
    PRODUCT_IDS.advanced,
    undefined,
    BOOKING_PACKAGE_TYPE.mri
  )
}

/**
 * The page's primary CTA — books the bloods + full-body MRI check-up where the
 * scan is sellable, and the blood test alone where it isn't.
 *
 * The category matters as much as the product: `category="mri"` is measured
 * against the market's EXPANSION flag, so on a blood-booking market it renders
 * the waitlist button instead of a booking link. Pointing the CTA at the blood
 * test is what makes the page's own primary action work there.
 *
 * `tone` is explicit rather than inferred: the same CTA appears on the light
 * process band (where aubergine `primary` is correct) and on the two dark photo
 * bands (where it would be aubergine-on-aubergine and stop reading as a
 * button).
 */
export function AthleteBookingCta({
  trackingLabel,
  label,
  tone = "onLight",
  showImaging,
  trackingLocation = "go/lauraphilipps",
}: {
  trackingLabel: string
  label: string
  tone?: "onLight" | "onDark"
  /** Is the full-body scan sellable in this market? */
  showImaging: boolean
  /** Where the click happened. Defaults to this campaign page; the German
   *  homepage runs the closing band too and must not report its clicks as
   *  campaign traffic. */
  trackingLocation?: string
}) {
  return (
    <BookingCta
      productId={showImaging ? PRODUCT_IDS.advanced : PRODUCT_IDS.bloodTest}
      category={showImaging ? "mri" : "blood"}
      trackingLabel={trackingLabel}
      trackingLocation={trackingLocation}
      className="block w-full sm:inline-block sm:w-auto"
    >
      <Button
        size="lg"
        variant={tone === "onDark" ? "on-dark" : "primary"}
        className="group w-full sm:w-auto"
        iconRight={
          <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
        }
      >
        {label}
      </Button>
    </BookingCta>
  )
}

/* ── The practical part — /dev/home-v3's four steps, set in a row: the heading
   centred, a picture per step and its caption underneath. Four across at
   `lg`, in pairs on a tablet. Where the scan is not sellable the scan step
   and the heading's scan line drop out, and the follow-up test takes the
   fourth place. ── */
export function AthleteSteps({ showImaging }: { showImaging: boolean }) {
  const t = useTranslations("go")
  const steps = STEPS.filter((step) =>
    showImaging ? !("bloodOnly" in step) : !("imaging" in step)
  )
  return (
    <section className="py-16 md:py-24">
      <div className={`${SECTION} flex flex-col items-center gap-12`}>
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="type-label-md text-neutral-600 uppercase">
            {t(k.athleteStepsEyebrow)}
          </p>
          <h2 className="type-display-md text-neutral-900">
            <span className="block">{t(k.athleteStepsHeadingBlood)}</span>
            {showImaging && (
              <span className="block">{t(k.athleteStepsHeadingScan)}</span>
            )}
            <span className="text-bright-blue-600 block">
              {t(k.athleteStepsHeadingAccent)}
            </span>
          </h2>
        </div>

        <ol className="grid w-full grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step) => (
            <li key={step.titleKey} className="flex flex-col">
              <div className="relative aspect-[3/2] w-full overflow-hidden rounded-[16px] bg-neutral-100">
                <Image
                  src={step.image}
                  alt={t(step.altKey)}
                  fill
                  sizes="(min-width: 1440px) 340px, (min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                  className="object-cover"
                />
              </div>
              <h3 className="type-title-lg mt-5 text-neutral-900">
                {t(step.titleKey)}
              </h3>
              <p className="type-body-lg mt-2 text-neutral-600">
                {t(step.bodyKey)}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

/* ── When the signs add up — the honest de-escalation that closes the signals
   beat and hands off to the reveal. Same treatment as the female-hormones
   signs section: sprout glyph, a two-line display heading whose second line
   takes the accent, then the qualifying line underneath.

   It was written to close the six signal cards, which the steps row has
   since replaced — so it now follows the quote with no signs before it.
   Keep, rewrite or drop is open. ── */
export function AthleteWedge() {
  const t = useTranslations("go")
  return (
    <section className="py-20 md:py-28">
      <div
        className={`${SECTION} flex flex-col items-center gap-5 text-center`}
      >
        <span className="text-neutral-900">
          <SproutIcon className="h-12 w-12" />
        </span>
        <p className="type-display-md max-w-[760px] text-neutral-900">
          {t(k.athleteWedgeHeading1)}
          <span className="text-bright-blue-600 block">
            {t(k.athleteWedgeHeading2)}
          </span>
        </p>
        <p className="type-title-lg max-w-[820px] font-medium text-neutral-600">
          {t(k.athleteSignalsSummaryBody)}
        </p>
      </div>
    </section>
  )
}

/* ── Fit does not mean healthy — the shared scroll-pinned immersive in its
   `split` form: the claim holds on the left and the four structural facts fade
   in beside it, so they read as support for the argument rather than four
   detached assertions after it. That also lets the runway drop from 240vh to
   150vh — the stacked form needed the length to play two beats, this plays
   one. ── */
export function AthleteReveal() {
  const t = useTranslations("go")
  return (
    <ImmersiveReveal
      image={PHOTOS.mountainRun}
      layout="split"
      trackVh={130}
      /* Anchored low and right so the runner sits under the content columns
         rather than behind either one. */
      objectPosition="70% 62%"
      /* An even wash, not the default left-weighted one: both columns carry
         copy here. The photo still leads — it is sharp and unwashed until the
         scroll starts, and the blur is what hands over to the content. */
      scrim="linear-gradient(90deg, rgba(0,0,0,0.80) 0%, rgba(0,0,0,0.62) 50%, rgba(0,0,0,0.52) 100%), linear-gradient(0deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 55%)"
      intro={
        <div className={`${SECTION} lg:pr-0`}>
          <div className="flex max-w-[600px] flex-col gap-4 sm:gap-6">
            <p className="type-label-md text-terracotta-200 uppercase">
              {t(k.athleteRevealEyebrow)}
            </p>
            <h2 className="type-display-md text-balance text-neutral-50">
              {t(k.athleteRevealHeading)}
            </h2>
            <p className="type-body-lg max-w-[520px] text-pretty text-white/85">
              {t(k.athleteRevealBody)}
            </p>
          </div>
        </div>
      }
    >
      <div className={`${SECTION} lg:pl-0`}>
        <div className="max-w-[560px]">
          <ul className="flex flex-col border-t border-white/20">
            {REVEAL_ROWS.map((rowKey) => (
              <li
                key={rowKey}
                className="type-title-md border-b border-white/20 py-3 font-medium text-white sm:py-4"
              >
                {t(rowKey)}
              </li>
            ))}
          </ul>
          <a
            href="#pricing"
            className="type-body-lg group mt-6 inline-flex h-[52px] items-center justify-center gap-2 rounded-full bg-white px-10 font-medium text-neutral-900 transition-colors hover:bg-white/90 sm:mt-8"
          >
            {t(k.athleteRevealCta)}
            <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </ImmersiveReveal>
  )
}

/* ── One draw, your whole body — the shared CMS-backed biomarker browser, so
   the panel this page advertises is the panel the CMS actually sells (nothing
   here is an authored marker list that could drift). Sits directly above the
   stepper: what you get, then how you get it. ── */
export function AthleteCoverage({ showImaging }: { showImaging: boolean }) {
  const t = useTranslations("go")
  const lp = useLocalePath()
  const { code: country } = useCountry()
  return (
    <section className="bg-neutral-50 pt-16 pb-10 md:pt-24 md:pb-14">
      <div className={SECTION}>
        <div className="mx-auto flex max-w-[720px] flex-col items-center gap-3 text-center md:gap-4">
          <p className="type-label-md text-neutral-600 uppercase">
            {t(k.athleteCoverageEyebrow)}
          </p>
          <h2 className="type-display-md text-neutral-900">
            {t(k.athleteCoverageHeading)}
          </h2>
          <p className="type-body-lg max-w-[560px] text-neutral-600">
            {t(
              showImaging
                ? k.athleteCoverageBody
                : k.athleteCoverageBodyBloodOnly
            )}
          </p>
        </div>
        <div className="mt-12">
          <BiomarkerDiseaseExplorer
            packages={[baseBloodTest(country).biomarkerPackage]}
            tabsAllowed={["biomarkers"]}
            defaultTab="biomarkers"
            hideHeader
            biomarkersTab={{
              cta: {
                label: t(k.bloodtestBiomarkersCta),
                href: lp("/biomarkers"),
                variant: "text",
              },
            }}
          />
        </div>
        <div className="mt-10 flex justify-center">
          <AthleteBookingCta
            trackingLabel="lauraphilipps_coverage_cta"
            label={t(k.athleteHeroCta1)}
            showImaging={showImaging}
          />
        </div>
      </div>
    </section>
  )
}

/* ── The offer — the blood-only tier beside the highlighted bloods + full-body
   MRI check-up. `highlight="mri"` moves the recommended treatment onto the
   scan card: unlike the concern LPs, the full check-up IS this page's pitch. ── */
export function AthletePricing({ showImaging }: { showImaging: boolean }) {
  const t = useTranslations("go")
  const fullCheckupHref = useFullCheckupHref()
  return (
    <section
      id="pricing"
      className="scroll-mt-24 pt-12 pb-16 md:pt-16 md:pb-24"
    >
      <div className={SECTION}>
        <div className="mx-auto max-w-[760px] text-center">
          <p className="type-label-md text-neutral-600 uppercase">
            {t(k.athleteOfferEyebrow)}
          </p>
          <h2 className="type-display-md mt-4 text-balance text-neutral-900">
            {t(
              showImaging
                ? k.athleteOfferHeading
                : k.athleteOfferHeadingBloodOnly
            )}
          </h2>
        </div>

        <div className="mt-14">
          {showImaging ? (
            <BloodTrackingComparison
              offer="blood-mri"
              highlight="mri"
              trackingLocation="go/lauraphilipps"
              baselineCtaLabel={t(k.athletePricingBaselineCta)}
              baselineProductId={PRODUCT_IDS.bloodTest}
              extraBaselineRows={[t(k.athletePricingSharedRow1)]}
              mriHref={fullCheckupHref}
              mriCardTagline={t(k.athletePricingMriTagline)}
              mriRows={[
                t(k.athletePricingMriRow1),
                t(k.athletePricingMriRow2),
                t(k.athletePricingMriRow3),
              ]}
            />
          ) : (
            /* Blood-only market: the standalone one-off card, the same single
               card /go/female-hormones falls back to. Nothing here mentions
               the scan, so there is no upgrade tier to compare against — the
               two facts under the shared row are already translated for the
               hero KPI strip and hold for the blood test on its own. */
            <BloodTrackingComparison
              showMembership={false}
              trackingLocation="go/lauraphilipps"
              baselineCtaLabel={t(k.athletePricingBaselineCta)}
              baselineProductId={PRODUCT_IDS.bloodTest}
              extraBaselineRows={[
                t(k.athletePricingSharedRow1),
                t(k.athleteHeroKpi3Value),
                t(k.athleteHeroKpi3Label),
              ]}
            />
          )}
        </div>

        <p className="type-body-md mx-auto mt-10 max-w-[680px] text-center text-neutral-500">
          {t(k.athleteOfferFootnote)}
        </p>
      </div>
    </section>
  )
}

/* ── Closing CTA — the Zell am See finish frame, treated so the photo leads.
   The wash is light and the blur is the immersive sections' static two-layer
   technique masked to wherever the copy is, rather than an animated filter.

   The brief was to foreground the Ahead logo on her suit, and the geometry is
   the whole job. The source is PORTRAIT (3299x4948) in a full-bleed landscape
   band, so the band shows a horizontal SLICE of it, and her rendered size is
   `bandWidth / 3299` and nothing else — it does not change with band height,
   and pre-cropping the asset would not change it either. The only lever is how
   much of her the slice contains.

   Measured off the source, as fractions of its height: raised hands 0.228,
   head 0.32 (the `PHOTO_SUBJECTS` entry), Ahead logo 0.49. All three have to
   survive the crop, so the slice has to be at least 0.262 of the source height
   — which, because her size tracks the width, means the band height needed
   grows with the screen.

   That is why the copy is a LEFT COLUMN at `lg` and not the centred block it
   is below it. Stacked, the band had to hold that 0.262 slice AND a 416px copy
   block underneath, and at no width did the sum fit a 1280x800 laptop — three
   attempts at crop-and-height arithmetic all rediscovered the same wall, and
   the last one fitted the laptop only by cutting her hands off at 1920. Beside
   her, the band carries the slice alone: 720 fits both a 1280x800 and the
   shorter 1512x760, with her hands 53-78px clear of the top edge and the logo
   73-139px clear of the bottom.

   Two crops, because the copy moves. Below `lg` it is bottom-anchored, so
   `46%` puts the logo above it. At `lg` nothing sits under her, so `30%` opens
   headroom for the hands instead. Above `2xl` the height tracks the viewport
   (`48vw`) rather than stepping, which holds the band-to-photo ratio constant
   — her face and the logo land at the same band fractions at 1920, 2560 and
   3440 instead of the logo drifting down as the screen widens.

   Her horizontal position is the other half of it: see `PHOTO_LAYER`. ── */
/**
 * Position of the closing band's sharp photo layer.
 *
 * At `lg` she is nudged right so the left copy column sits on background
 * rather than on her. `object-position` cannot do this: the band is wider than
 * the portrait source, so `object-cover` fills the width exactly and the X
 * component has nothing to slide. Widening the layer would shift her only half
 * as far per unit of growth and spend the rest enlarging her — which costs
 * band height she does not have below `2xl` — so the layer keeps its width and
 * moves.
 *
 * Only this layer moves. The blurred layer behind it stays full-bleed, which
 * is what fills the 6% the sharp one vacates on the left: offsetting both left
 * a strip of bare `bg-aubergine-950` there, and it read as a hard vertical
 * seam at 1920 even under the wash. The price is that the blurred copy of her
 * shoulder sits 6% left of the sharp one, so it can ghost through where the
 * mask is still partly opaque — but the mask is down to a third by the time it
 * reaches her (0.385 of the source) and out entirely by 0.44, and what it
 * ghosts over there is out-of-focus crowd.
 */
const PHOTO_LAYER = "absolute inset-y-0 left-0 w-full lg:left-[6%]"

export function AthleteClosingCta({
  showImaging,
  trackingLabel = "lauraphilipps_bottom_cta",
  trackingLocation,
  copy = "campaign",
}: {
  showImaging: boolean
  /** Overridden where the band runs outside the campaign page, so the two
   *  placements stay separable in the funnel. */
  trackingLabel?: string
  trackingLocation?: string
  /** Which copy the band carries. On the campaign page it is the closing
   *  argument, so it answers a page the reader has already read: "Trainieren
   *  Sie mit Fakten". On the homepage it is the first time Laura appears, and
   *  a closing argument has nothing to close, so `"homepage"` leads with her
   *  name and her routine instead. Its own keys rather than a rewrite of the
   *  campaign ones: the campaign hero already carries the "check-up she
   *  trusts" line, and reusing it for the bottom band would print the same
   *  heading twice on that page. */
  copy?: "campaign" | "homepage"
}) {
  const t = useTranslations("go")
  /* Blur only where the copy sits, so she stays sharp. Two masks because the
     copy moves: below `lg` it is bottom-anchored and the floor is blurred; at
     `lg` it is a left column, so the blur is too. Each is shared by its
     blurred layer and that layer's mask, so the two can't drift.

     `LEFT_MASK` has to clear her face, which is the point of the frame. Her
     visor starts at 0.413 of the source width and `PHOTO_LAYER` nudges the
     photo 6% right at `lg`, so it starts at 0.473 of the band — hence a ramp
     that is finished by 0.44. That leaves it overlapping the last inch of the
     copy column (whose right edge runs 0.35-0.44 of the band depending on
     width), so the text's contrast comes mostly from the radial field below
     rather than from the blur: between the two, the face is the harder
     constraint. `lauraphilipps-responsive.spec.ts` holds both edges, because
     a blur creeping back onto her face is invisible to a framing check —
     she is exactly where she should be, just veiled. */
  const FLOOR_MASK =
    "linear-gradient(to top, black 0%, black 26%, transparent 58%)"
  const LEFT_MASK =
    "linear-gradient(to right, black 0%, black 28%, transparent 44%)"
  return (
    <section className="bg-aubergine-950 relative isolate flex min-h-[900px] items-end overflow-hidden text-white md:min-h-[860px] lg:min-h-[720px] lg:items-center 2xl:min-h-[max(720px,48vw)]">
      <div aria-hidden className={PHOTO_LAYER}>
        <Image
          src={PHOTOS.zellamSeeFinish}
          alt=""
          fill
          sizes="100vw"
          className="object-cover object-[50%_46%] lg:object-[50%_30%]"
        />
      </div>
      {/* Static progressive blur — a pre-blurred copy masked to the floor. Not
          `backdrop-blur` (a second compositing layer per frame) and not an
          animated `filter`, per the immersive-reveal contract. */}
      <div
        aria-hidden
        className="absolute inset-0 lg:hidden"
        style={{
          maskImage: FLOOR_MASK,
          WebkitMaskImage: FLOOR_MASK,
          filter: "blur(14px)",
        }}
      >
        <Image
          src={PHOTOS.zellamSeeFinish}
          alt=""
          fill
          sizes="100vw"
          className="object-cover object-[50%_46%]"
        />
      </div>
      <div
        aria-hidden
        className="absolute inset-0 hidden lg:block"
        style={{
          maskImage: LEFT_MASK,
          WebkitMaskImage: LEFT_MASK,
          filter: "blur(14px)",
        }}
      >
        <Image
          src={PHOTOS.zellamSeeFinish}
          alt=""
          fill
          sizes="100vw"
          className="object-cover object-[50%_30%]"
        />
      </div>
      {/* Light bottom-up wash — enough to ground the block, not enough to bury
          the strongest photo on the page. */}
      <div
        aria-hidden
        className="from-aubergine-950/80 via-aubergine-950/22 absolute inset-0 bg-gradient-to-t via-40% to-transparent lg:bg-gradient-to-r"
      />
      {/* Contained field behind the copy. This frame is bright and busy right
          where the centred block sits (pink tape, red carpet, her lower body),
          so the light wash alone does not carry the text. A flat darker wash
          would fix that by burying the whole photo — the thing this treatment
          exists not to do — so the darkening is local and soft-edged, leaving
          her, the tape and the logo clean. */}
      <div
        aria-hidden
        className="absolute inset-0 lg:hidden"
        style={{
          background:
            "radial-gradient(ellipse 64% 46% at 50% 78%, rgba(41,14,42,0.80) 0%, rgba(41,14,42,0.64) 45%, rgba(41,14,42,0) 80%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 hidden lg:block"
        style={{
          background:
            "radial-gradient(ellipse 44% 70% at 22% 50%, rgba(41,14,42,0.82) 0%, rgba(41,14,42,0.62) 45%, rgba(41,14,42,0) 82%)",
        }}
      />
      <div
        className={`${SECTION} relative z-10 w-full pt-16 pb-14 md:pb-16 lg:py-0`}
      >
        <div className="mx-auto flex max-w-[680px] flex-col items-center text-center lg:mx-0 lg:max-w-[460px] lg:items-start lg:text-left">
          {/* Shares the hero's eyebrow, so it needs the hero's gate too: the
              base string names the scan, and this block is the last thing a
              blood-only market reads. */}
          <p className="type-label-md text-white uppercase">
            {t(
              showImaging ? k.athleteHeroEyebrow : k.athleteHeroEyebrowBloodOnly
            )}
          </p>
          <h2 className="type-display-md mt-5 text-balance text-white">
            {t(
              copy === "homepage"
                ? k.athleteHomepageCtaHeading
                : k.athleteCtaHeading
            )}
          </h2>
          {/* The homepage line names no marker count and no scan, so it needs
              no imaging gate: the band runs there only in Germany, which sells
              the blood check-up alone. */}
          <p className="type-body-lg mt-5 max-w-[540px] text-white">
            {t(
              copy === "homepage"
                ? k.athleteHomepageCtaBody
                : showImaging
                  ? k.athleteCtaBody
                  : k.athleteCtaBodyBloodOnly
            )}
          </p>
          <div className="mt-10 flex flex-col items-center gap-5 lg:items-start">
            <AthleteBookingCta
              trackingLabel={trackingLabel}
              trackingLocation={trackingLocation}
              label={t(k.athleteHeroCta1)}
              tone="onDark"
              showImaging={showImaging}
            />
            <p className="type-body-sm text-white/85">
              {t(k.athleteCtaFinePrint)}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
